import React, { useState, ReactElement } from 'react';
import styled from '@emotion/styled';
import Tooltip from 'rc-tooltip';
import 'rc-tooltip/assets/bootstrap_white.css';

import { getBrowserDetails } from '../utils/helpers';
import { getTextColor } from '../style';
import { IInputLabelProps } from './types/InputLabel';
import { ITooltip } from './types';

const InputLabelContainer = styled('div')<{ isBilling: boolean }>`
  position: relative;
  margin-bottom: ${props => (props.isBilling ? '4px' : '10px')};
`;

const LabelDescription = styled('div')`
  padding-top: 6px;
  font-weight: normal;
  font-size: 12px;
`;

const Label = styled('label')`
  display: inline-block;
  margin-top: 0;
  font-weight: 400;
  position: relative;
  margin-right: 16px;
`;

const BillingLabel = styled('label')`
  display: inline-block;
  margin-top: 0;
  font-weight: 400;
  position: relative;
  margin-right: 16px;
`;

const LabelToolTip = styled('span')`
  position: absolute;
  padding-bottom: 10px;
  padding-top: 10px;
  margin-top: -10px;
  cursor: pointer;
`;

const RedRequired = styled('span')(() => ({
  ...getTextColor().RED,
}));

const InputLabel = ({
  label,
  labelDescription,
  tooltip,
  inputId,
  customStyle = {},
  isBilling = false,
  customContainerStyle = {},
  isRequired = false,
  extraLabelInfo,
}: IInputLabelProps): ReactElement => {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleClickPrevent = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
  };

  const browserDetails = getBrowserDetails();

  const onVisibleChange = (visible: boolean) => {
    setShowTooltip(visible);
  };

  const renderToolTip = (tooltipProps: ITooltip) => {
    const { icon, iconWidth, text, overlayStyle, placement, trigger } = tooltipProps;

    const otherProps = {};

    // Apply mobile-specific behavior only if trigger is not explicitly provided
    if (!trigger || trigger?.length === 0) {
      Object.assign(
        otherProps,
        browserDetails.isMobile ? { onVisibleChange, visible: showTooltip } : { trigger: 'hover' }
      );
    }

    // Only use the custom trigger if explicitly provided
    if (trigger && trigger?.length > 0) {
      Object.assign(otherProps, { onVisibleChange, trigger, visible: showTooltip });
    }

    return (
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      <Tooltip
        placement={placement ? placement : 'top'}
        arrowContent={<div className="rc-tooltip-arrow-inner" />}
        overlay={text ? text : ''}
        overlayStyle={overlayStyle ? overlayStyle : null}
        {...otherProps}
      >
        <LabelToolTip
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          iconWidth={iconWidth}
          onClick={trigger && trigger?.length > 0 ? handleClickPrevent : () => onVisibleChange(!showTooltip)}
        >
          {icon}
        </LabelToolTip>
      </Tooltip>
    );
  };

  const setToolTip = tooltip ? renderToolTip(tooltip) : null;

  return (
    <InputLabelContainer isBilling={isBilling} style={{ ...customContainerStyle }}>
      {isBilling ? (
        <BillingLabel style={customStyle} data-test-id={inputId}>
          {label}
          {isRequired ? <RedRequired>*</RedRequired> : ''}
          {setToolTip}
        </BillingLabel>
      ) : (
        <Label style={customStyle} data-test-id={inputId}>
          {label}
          {isRequired ? <RedRequired>*</RedRequired> : ''}
          {setToolTip}
        </Label>
      )}
      {extraLabelInfo && extraLabelInfo}
      {labelDescription ? <LabelDescription data-test-id={inputId}>{labelDescription}</LabelDescription> : null}
    </InputLabelContainer>
  );
};

export default InputLabel;
